import { Component, computed, inject, signal, ViewChild, WritableSignal } from '@angular/core';
import {GameService} from "../../services/game.service";
import {WheelSelectorChangeEvent, WheelSelectorComponent} from "../wheel-selector/wheel-selector.component";
import {PlayResult} from "../../model/playResult";
import {PenaltyDialogOptions} from "../../model/penaltyDialog";
import {calculateYardLine, getYardIndex, selectProperWheelYardLine, toggleYardLine} from "../../helpers/distance";
import {IncreaseDownCommand} from "../../commands/IncreaseDownCommand";
import {FirstDownCommand} from "../../commands/FirstDownCommand";
import {RepeatLastDownCommand} from "../../commands/RepeatLastDownCommand";
import {SetOdkCommand} from "../../commands/SetOdkCommand";
import {CalculateDistanceCommand} from "../../commands/CalculateDistanceCommand";
import {CalculateYardLineCommand} from "../../commands/CalculateYardLineCommand";
import {ApplyDownRulesCommand} from "../../commands/ApplyDownRulesCommand";
import {Command} from "../../commands/Command";
import {SetPlayResultCommand} from "../../commands/SetPlayResultCommand";
import {SetDownCommand} from "../../commands/SetDownCommand";
import {Down} from "../../model/down";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions } from "@angular/material/dialog";
import {SetPenaltyCommand} from "../../commands/SetPenaltyCommand";
import {SetDistanceCommand} from "../../commands/SetDistanceCommand";
import {SetPenaltyAfterPlayCommand} from "../../commands/SetPenaltyAfterPlayCommand";
import {SetYardLineCommand} from "../../commands/SetYardLineCommand";
import {PlayType} from "../../model/playType";
import {PenaltyType} from "../../model/penaltyType";
import { TranslocoService, TranslocoModule } from "@ngneat/transloco";
import {MessageDialogService} from "../../services/message-dialog.service";
import { CdkScrollable } from '@angular/cdk/scrolling';
import { SousTitreBlocComponent } from '../sous-titre-bloc/sous-titre-bloc.component';
import { ButtonToggleGroupComponent } from '../button-toggle-group/button-toggle-group.component';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-penalty',
    templateUrl: './penalty.component.html',
    styleUrls: ['./penalty.component.scss'],
    imports: [CdkScrollable, MatDialogContent, SousTitreBlocComponent, ButtonToggleGroupComponent, MatButtonToggleGroup, MatButtonToggle, MatButton, WheelSelectorComponent, MatDialogActions, TranslocoModule]
})
export class PenaltyComponent {
  game = inject(GameService);
  dialogRef = inject<MatDialogRef<PenaltyComponent>>(MatDialogRef);
  dataPenalty = inject<PenaltyDialogOptions>(MAT_DIALOG_DATA);
  protected translocoService = inject(TranslocoService);

  penaltyForHome: WritableSignal<boolean> = signal(true);
  currentIndex: WritableSignal<number> = signal(0)
  messageDialog = inject(MessageDialogService);
  data!: any[]
  title = ''
  resultingDown = ''
  finishTagOnClose = false
  initYardline: string
  penaltyYard = 0
  yardSpot = ''
  protected readonly PenaltyType = PenaltyType;

  tempCommands: Command[] = []
  tempCommandsAfterTagging: Command[] = []

  @ViewChild('wheelSelector') wheelSelector!: WheelSelectorComponent;

  constructor() {
    const dataPenalty = this.dataPenalty;

    this.finishTagOnClose = dataPenalty.finishTagOnClose || false
    this.initYardline = dataPenalty.initYardline || this.game.getFirstPendingGamePlay().yardLine
    this.init();
  }

  init() {
    const down = this.game.getFirstPendingGamePlay().down;
    if (down === undefined) {
      throw new Error('Aucun down')
    }

    this.currentIndex.set(getYardIndex(this.initYardline))

    this.title = 'Spot the Ball'
    // Données du wheelSelector
    this.data = [{
      list: selectProperWheelYardLine('default'),
      currentIndex: this.currentIndex(),
      text: 'yard'
    }]
  }

  SEUIL_HALF_THE_DISTANCE_TD = '30';
  SEUIL_HALF_THE_DISTANCE_SF = '-30';

  isHalfTheDistanceDisabled = computed(() => {
    if (this.game.homeHasPossession()) {
      if (this.penaltyForHome()) {
        // on recule
        if (this.currentIndex() < getYardIndex(this.SEUIL_HALF_THE_DISTANCE_SF)) {
          return false
        }
      } else {
        // penalité pour away (on avance)
        if (this.currentIndex() > getYardIndex(this.SEUIL_HALF_THE_DISTANCE_TD)) {
          return false
        }
      }

      return true
    }

    // Away à la possession
    if (this.penaltyForHome()) {
      // on avance
      if (this.currentIndex() > getYardIndex(this.SEUIL_HALF_THE_DISTANCE_TD)) {
        return false
      }
    } else {
      // penalité pour away (on recule)
      if (this.currentIndex() < getYardIndex(this.SEUIL_HALF_THE_DISTANCE_SF)) {
        return false
      }
    }

    return true
  })

  change (obj: WheelSelectorChangeEvent) {
    this.yardSpot = obj.value
  }

  setPenaltyForHome() {
    this.penaltyForHome.set(true)
    this.recalculerSpotTheBall()
  }
  setPenaltyForAway() {
    this.penaltyForHome.set(false)
    this.recalculerSpotTheBall()
  }

  repeatLastDown() {
    this.resultingDown = 'repeatLastDown'
    this.recalculerSpotTheBall()
  }

  firstDown() {
    this.resultingDown = 'firstDown'
    this.recalculerSpotTheBall()
  }

  /**
   * On perd le jeu courant
   */
  loosDown() {
    this.resultingDown = 'loosDown'
    this.recalculerSpotTheBall()
  }

  calculateYardFn: any = () => {}

  selectYard(gnLs: number) {
    this.calculateYardFn = () => { this.setYard(gnLs) }

    this.recalculerSpotTheBall()
  }

  protected setYard(gnLs: number) {
    console.log('setYard...', this.initYardline)
    this.penaltyYard = this.isPenaltyOnPossessingTeam() ? gnLs * -1 : gnLs;

    this.yardSpot = calculateYardLine(this.initYardline, this.penaltyYard)
    console.log(this.initYardline, this.penaltyYard, '=> yardSpot', this.yardSpot)
    this.wheelSelector.setValue(0, this.yardSpot.toString())
  }

  isPenaltyOnPossessingTeam(): boolean {
    if (this.game.homeHasPossession() && this.penaltyForHome()) return true
    if (!this.game.homeHasPossession() && !this.penaltyForHome()) return true

    return false
  }

  selectHalfTheDistance() {
    this.calculateYardFn = () => { this.setHalfTheDistance(); }

    this.recalculerSpotTheBall()
  }

  recalculerSpotTheBall() {
    if (this.calculateYardFn) {
      this.calculateYardFn()
    }
  }

  private setHalfTheDistance() {
    const TD_INDEX = 110;
    const SF_INDEX = 0;

    const yardLine = this.game.getFirstPendingGamePlay().yardLine;
    const yardIndex = getYardIndex(yardLine)

    let gnLs = 0

    const conserverYardLine = yardLine === '-1' || yardLine === '1'

    if (!conserverYardLine) {
      if (yardIndex < getYardIndex(this.SEUIL_HALF_THE_DISTANCE_SF)) {
        const distanceFromSF = SF_INDEX - yardIndex;
        gnLs = Math.floor(distanceFromSF / 2) * -1
      } else {
        const distanceFromTD = TD_INDEX - yardIndex;
        gnLs = Math.ceil(distanceFromTD / 2)
      }
    }

    this.selectYard(gnLs)

    this.resultingDown = 'halfTheDistance'

    this.tempCommandsAfterTagging.push(
      new SetDownCommand(this.game.getNextGamePlay(), Down.D1),
      new SetDistanceCommand(this.game.getNextGamePlay(), gnLs),
      new CalculateYardLineCommand(this.game.getNextGamePlay(), this.game.getFirstPendingGamePlay())
    );
  }

  save(): void {
    if (!this.isValid()){
      return
    } else{
      const currentGamePlay = this.game.getFirstPendingGamePlay()
      if (!currentGamePlay?.playType) currentGamePlay.playType = PlayType.Run
      if(this.finishTagOnClose) {
        this.addResultingDownCommands();
        this.game.addCommand([
          new SetPlayResultCommand(currentGamePlay, PlayResult.Penalty),
          new SetPenaltyCommand(currentGamePlay, this.penaltyForHome(), this.penaltyYard, this.yardSpot),
        ])
        this.game.addCommandsAfterTagging(this.tempCommandsAfterTagging)
        this.game.finishTagging(currentGamePlay)
        this.game.notifyGamePlayUpdate()
        this.dialogRef.close({navigateOnClose: true});

      } else {
        // Jeu avec changement de possession (ex: botté bloqué récupéré) : le terrain
        // est inversé au finish, donc on inverse ici le yardLine du prochain jeu pour
        // ne pas écraser ce flip avec une valeur calculée dans le repère courant.
        const nextYardLine = this.dataPenalty.flipNextYardLine
          ? toggleYardLine(this.yardSpot)
          : this.yardSpot

        this.game.addCommandsAfterTagging([
          new SetPenaltyAfterPlayCommand(currentGamePlay, this.penaltyForHome(), this.penaltyYard, this.yardSpot),
          new SetYardLineCommand(this.game.getNextGamePlay(), nextYardLine),
          new SetDistanceCommand(this.game.getNextGamePlay(), this.penaltyYard),
          new FirstDownCommand(this.game.getNextGamePlay()),
        ])

        this.dialogRef.close({navigateOnClose: false});
      }
    }
  }

  cancel(): void {
    this.dialogRef.close({navigateOnClose: false});
  }

  protected addResultingDownCommands() {
    if (!this.resultingDown) return

    const currentGamePlay = this.game.getFirstPendingGamePlay();
    const nextGamePlay = this.game.getNextGamePlay(currentGamePlay)

    const resultingDownCommands: any = {
      repeatLastDown: () => {
        this.tempCommandsAfterTagging.push(...[
          new RepeatLastDownCommand(nextGamePlay, currentGamePlay),
          new SetOdkCommand(nextGamePlay, currentGamePlay.odk),
          new CalculateDistanceCommand(nextGamePlay, currentGamePlay),
          new CalculateYardLineCommand(nextGamePlay, currentGamePlay)
        ]);
      },
      firstDown: () => {
        this.tempCommandsAfterTagging.push(
            new FirstDownCommand(this.game.getNextGamePlay(this.game.getFirstPendingGamePlay())),
            new SetOdkCommand(nextGamePlay, currentGamePlay.odk),
            new CalculateYardLineCommand(nextGamePlay, currentGamePlay)
        );
      },
      loosDown: () => {
        this.tempCommandsAfterTagging.push(...[
          new IncreaseDownCommand(nextGamePlay, this.game.getFirstPendingGamePlay()),
          new CalculateDistanceCommand(nextGamePlay, currentGamePlay),
          new ApplyDownRulesCommand(nextGamePlay, currentGamePlay, this.game.getNumberOfDowns())
        ]);
      },
      halfTheDistance: () => {
        return
      }
    };
    resultingDownCommands[this.resultingDown]();
  }

  validation(valid: boolean, message: any[]) {
    if (this.dataPenalty.penaltyType !== PenaltyType.AfterPlay) {
      if (!this.resultingDown) {
         valid = false
        const texte = this.translocoService.translate('components.penalty.validation.resultingDown');
        message.push(texte)
      }
    }
    return valid;
  }

  isValid(): boolean {
    let valid = true
    const message: string[] = []
    valid = this.validation(valid, message);

    if (valid) {
      return true
    }

    this.messageDialog.openMessage(
      'validation.errorTitle',
      message,
      'app.ok',
      'error',
    )
    return false
  }
}
